import type { ApprovalDetail, DocumentView } from '@myrewards/contracts';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-aria-components';
import {
  Button,
  Card,
  cx,
  DataTable,
  EmptyState,
  formatCount,
  formatDate,
  formatMoney,
  organizationDate,
  plural,
  scoped,
  StatusPill,
} from '../../ui';
import { meQuery } from '../../api';
import { budgetsForYearQuery, poolLedgerQuery } from '../../api/settings';
import { executivesCheckWords, lettersGoTo } from '../../awards/present';
import { signedMoney } from '../../settings/model';
import { amountWords, TYPE_LABEL } from '../labels';
import { AmountInput, CountInput, FileList, Notice } from '../shared';
import {
  afterTopUp,
  changedSinceSent,
  decidesOwn,
  demandOf,
  isDefaultTopUp,
  overageLedger,
  peopleSummary,
  recentWords,
  repeatPill,
  repeatRuleWords,
  reviewsPaaValue,
  sentWords,
  topUpSentence,
  type TopUpDraft,
} from './presenter';
import s from '../Ops.module.css';

type Line = ApprovalDetail['lines'][number];

const costType = (line: Line): string =>
  line.coding === null ? 'Not coded yet' : line.coding.costType === 'GA' ? 'G&A' : 'Overhead';

/**
 * The arrow between an amount before and after: drawn by CSS, so it's seen but never in the text.
 * Screen readers, a copy and a find get "$161,405 then $156,905", never "$161,405 →  then $156,905"
 * (APP-254's finding, fixed as APP-248 drew Home's dot).
 */
export function ThenArrow(): ReactElement {
  return (
    <>
      <span className={s.thenArrow} aria-hidden="true" />
      <span className="visually-hidden"> then </span>
    </>
  );
}

export function chargeWords(line: Line): string {
  if (line.coding === null) return 'Not coded yet';
  return [line.coding.project, line.coding.account, line.coding.org]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

/**
 * The one charge every person on the award shares, for the summary (SL7 shows it once, beside the
 * program). Null when they differ (a TAA across two pools, mixed OH and G&A), and then each line
 * keeps its own Charge column.
 */
export function uniformCharge(detail: ApprovalDetail): string | null {
  const lines = detail.lines.filter((l) => l.included);
  const first = lines[0];
  if (first === undefined || first.coding === null) return null;
  const words = chargeWords(first);
  return lines.every((l) => l.coding !== null && chargeWords(l) === words) ? words : null;
}

/** The award at a glance: type, number, who entered it and when, program and award date. */
export function SummaryCard({
  detail,
  status,
  children,
}: {
  detail: ApprovalDetail;
  status: { label: string; tone: 'waiting' | 'info' | 'done' | 'problem' | 'neutral' };
  children?: ReactNode;
}): ReactElement {
  const { award } = detail;
  const by = award.onBehalfOf
    ? `${award.createdBy.name} for ${award.onBehalfOf.name}`
    : award.createdBy.name;
  const charge = uniformCharge(detail);
  return (
    <Card
      title={award.displayId ?? 'This award'}
      accent="gold"
      help={{
        shows: `The ${TYPE_LABEL[award.type]} waiting for this decision: who entered it, when, and what it’s for.`,
        canDo: 'Read it through, then decide below. Open the full record for its history.',
      }}
      aside={<StatusPill tone={status.tone} label={status.label} />}
    >
      <p className={cx('eyebrow', s.eyebrowMuted)}>{TYPE_LABEL[award.type]}</p>
      <p className={s.cardText}>
        Entered by <span className={s.strong}>{by}</span>
        {award.submittedAt === null ? '' : ` · sent ${formatDate(award.submittedAt)}`}
        {' · '}
        {peopleSummary(detail)}
      </p>
      <dl className={s.facts}>
        <div>
          <dt className={cx('eyebrow', s.fieldLabel)}>Program</dt>
          <dd className={s.factValue}>{award.program?.name ?? award.title ?? 'No program'}</dd>
        </div>
        <div>
          <dt className={cx('eyebrow', s.fieldLabel)}>Sector</dt>
          <dd className={s.factValue}>{award.sector?.name ?? 'Executive'}</dd>
        </div>
        <div>
          {/* SL7: the holder chosen at entry (QA-004), so the approver sees it (QA-029). */}
          <dt className={cx('eyebrow', s.fieldLabel)}>Letters go to</dt>
          <dd className={s.factValue}>{lettersGoTo(award.letters)}</dd>
        </div>
        <div>
          <dt className={cx('eyebrow', s.fieldLabel)}>Award date</dt>
          <dd className={s.factValue}>
            {award.awardDate === null ? 'Not set yet' : formatDate(award.awardDate)}
          </dd>
        </div>
        {charge !== null && (
          <div>
            <dt className={cx('eyebrow', s.fieldLabel)}>Charge</dt>
            <dd className={cx(s.factValue, s.code)}>{charge}</dd>
          </div>
        )}
      </dl>
      {children}
      <Link href={`/awards/${award.id}`} className={s.inlineLink}>
        Open the full record
      </Link>
    </Card>
  );
}

/**
 * Everyone on the award with their saved amounts (read-only: SL7, X2), what was sent when it was
 * changed while it waited, and, for approvers who see the team's recognition, each person's last
 * 12 months under the repeat-recipient rule (QA-006).
 */
export function PeopleCard({ detail }: { detail: ApprovalDetail }): ReactElement {
  const { data: me } = useQuery(meQuery);
  // A Sector Lead's team recognition, else the heat map for those who have Insights.
  const recognitionHref = me?.roles.some((r) => r.role === 'sector_lead')
    ? '/insights/team'
    : me?.nav.includes('insights') === true
      ? '/insights/heat-map'
      : undefined;
  const lines = detail.lines.filter((l) => l.included);
  const cash = lines.reduce((sum, l) => sum + l.cashCents, 0);
  const options = lines.reduce((sum, l) => sum + l.options, 0);
  const anyOptions = options > 0;
  const rule = detail.repeatRule;
  const recentColumn =
    rule === null
      ? []
      : [
          {
            id: 'recent',
            header: `Last ${plural(rule.months, 'month', 'months')}`,
            cell: (l: Line) => {
              if (l.recent === null) return <span className={s.muted}>Outside your sector</span>;
              const pill = repeatPill(l.recent);
              return (
                <span className={s.recentCell}>
                  <span>{recentWords(l.recent, rule)}</span>
                  {pill !== null && <StatusPill tone="waiting" size="sm" label={pill} />}
                </span>
              );
            },
          },
        ];
  return (
    <Card
      title={`People · ${formatCount(lines.length)}`}
      help={{
        shows:
          rule === null
            ? 'Each person on the award with their cost type, cash, options and the pool that pays.'
            : 'Each person on the award with their cost type, cash and options, and the awards they’ve had in the last 12 months.',
        canDo:
          rule === null
            ? 'Check who is on it and how much each gets before you decide.'
            : 'Check who is on it and how much each gets. A “Would be Repeat” pill means this award makes them a repeat recipient.',
      }}
      aside={
        <span className={s.muted}>
          {anyOptions ? 'Cash and options' : 'Cash only · no options'}
        </span>
      }
    >
      <DataTable
        caption={`People on ${detail.award.displayId ?? 'this award'}`}
        rows={lines}
        rowKey={(l) => l.id}
        minWidth={640}
        cards
        empty={{
          title: 'No one to review right now.',
          body: 'Each entry lists its people here, with their amounts.',
        }}
        columns={[
          {
            id: 'person',
            header: 'Person',
            card: 'title',
            cell: (l) => (
              <>
                <span className={s.strong}>{l.personName}</span>
                {l.jobTitle !== null && <span className={s.sub}>{l.jobTitle}</span>}
              </>
            ),
          },
          { id: 'type', header: 'Cost type', cell: costType },
          {
            id: 'cash',
            header: 'Cash',
            numeric: true,
            cell: (l) => (
              <>
                {formatMoney(l.cashCents)}
                {changedSinceSent(l) && (
                  <span className={s.sub}>{sentWords(l, reviewsPaaValue(detail))}</span>
                )}
              </>
            ),
          },
          {
            id: 'options',
            header: 'Options',
            numeric: true,
            cell: (l) => (l.options === 0 ? 'None' : formatCount(l.options)),
          },
          ...recentColumn,
          // One shared charge shows once, in the summary; lines that differ keep their own.
          ...(uniformCharge(detail) === null
            ? [
                {
                  id: 'charge',
                  header: 'Charge',
                  cell: (l: Line) => <span className={s.code}>{chargeWords(l)}</span>,
                },
              ]
            : []),
        ]}
        total={{
          person: peopleSummary(detail),
          cash: formatMoney(cash),
          options: options === 0 ? 'None' : formatCount(options),
        }}
      />
      {rule !== null && (
        <p className={s.muted}>
          {repeatRuleWords(rule)}.{' '}
          {recognitionHref !== undefined && (
            <Link href={recognitionHref} className={s.inlineLink}>
              See team recognition
            </Link>
          )}
        </p>
      )}
    </Card>
  );
}

/** Each pool's balance before and after this award (SL7 "Budget impact", X3 "Pools this touches"). */
export function BudgetCard({
  detail,
  title = 'Budget impact',
  note,
}: {
  detail: ApprovalDetail;
  title?: string;
  note?: string;
}): ReactElement {
  const year = detail.award.fiscalYear ?? null;
  // Each pool's budget for the year, for the bar: what's left after this award, and this award.
  const { data: positions } = useQuery({
    ...budgetsForYearQuery(year ?? 0),
    enabled: year !== null,
    retry: false,
  });
  const budgetOf = (poolId: string): number | null =>
    positions?.pools.find((p) => p.pool.id === poolId)?.budget?.cashCents ?? null;
  // A team award is cash only, so it counts no options (APP-070 D4).
  const withOptions = detail.award.type !== 'TAA';
  const yearWords = year === null ? 'this year' : String(year);
  // APP-200: a pool the Executives check, named for whoever doesn't decide this award.
  const checked = detail.award.calculation.checkedByExecutives ?? [];
  return (
    <Card
      title={title}
      titleSize="md"
      help={{
        shows:
          'Each pool this award draws on: what’s left this year before it and after it, from the saved amounts.',
        canDo:
          'Check it stays within budget. An award that goes over goes on to the Executives, who approve a top-up.',
      }}
    >
      {detail.budget.length === 0 && checked.length > 0 ? null : detail.budget.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="pool"
          size="sm"
          headline="Nothing pending against a pool"
          body="Each entry shows its effect on its pools here before you decide."
        />
      ) : (
        <ul className={s.poolList}>
          {detail.budget.map((pool) => {
            const demand = demandOf(detail, pool.pool.id);
            const after = pool.remainingAfter;
            const budget = budgetOf(pool.pool.id);
            const share = (cents: number): string =>
              budget === null || budget <= 0
                ? '0%'
                : `${Math.max(0, Math.min(100, (cents / budget) * 100)).toFixed(1)}%`;
            return (
              <li key={pool.pool.id} className={s.poolRow}>
                <span className={s.poolName}>
                  {scoped(pool.pool.name, `cash left ${yearWords}`)}
                </span>
                {pool.status === 'not_set' || after === null ? (
                  <>
                    <span className={s.muted}>Budget not set for this year</span>
                    <StatusPill tone="problem" size="sm" label="Budget not set" />
                    <Link href="/manage/executive/budget-pools" className={s.inlineLink}>
                      Set it in Executive › Budget pools
                    </Link>
                  </>
                ) : (
                  <>
                    <span className={s.poolMove}>
                      {formatMoney(after.cashCents + demand.cashCents)}
                      <ThenArrow />
                      <span className={cx(s.strong, after.cashCents < 0 && s.negative)}>
                        {formatMoney(after.cashCents)}
                      </span>
                    </span>
                    {budget !== null && budget > 0 && (
                      // What's left after this award, then this award, out of the year's budget.
                      <span className={s.poolBar} aria-hidden="true">
                        <span
                          className={s.barCyan}
                          style={{ width: share(Math.max(0, after.cashCents)) }}
                        />
                        <span
                          className={s.barGold}
                          style={{
                            width: share(
                              Math.min(
                                demand.cashCents,
                                Math.max(0, after.cashCents + demand.cashCents),
                              ),
                            ),
                          }}
                        />
                      </span>
                    )}
                    {withOptions && (
                      <span className={s.poolOptions}>
                        <span>{scoped(pool.pool.name, 'options left')}</span>
                        <span className={s.poolMove}>
                          {formatCount(after.options + demand.options)}
                          <ThenArrow />
                          <span className={cx(after.options < 0 && s.negative)}>
                            {formatCount(after.options)}
                          </span>
                        </span>
                      </span>
                    )}
                    <StatusPill
                      tone={pool.status === 'over_budget' ? 'problem' : 'done'}
                      size="sm"
                      label={
                        pool.status === 'over_budget'
                          ? `Over budget by ${amountWords(pool.shortfall?.cashCents ?? 0, pool.shortfall?.options ?? 0)}`
                          : 'Within budget'
                      }
                    />
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {checked.length > 0 && (
        <p className={s.muted} role="note">
          {executivesCheckWords(detail.award.type, checked)}
        </p>
      )}
      {note !== undefined && <p className={s.muted}>{note}</p>}
    </Card>
  );
}

/** What the person who entered it wrote, and the files they attached (SL7 "From Sam"). */
export function NotesCard({
  detail,
  documents,
}: {
  detail: ApprovalDetail;
  documents: readonly DocumentView[];
}): ReactElement {
  const { award } = detail;
  const first = award.createdBy.name.split(' ')[0] ?? award.createdBy.name;
  const text = award.justification ?? award.writeUpText;
  const files = documents.filter((d) => d.purpose !== 'tax_estimate' && d.purpose !== 'letter');
  return (
    <Card
      title={`From ${first}`}
      titleSize="md"
      help={{
        shows: `The note and files ${award.createdBy.name} added to this entry.`,
        canDo: 'Read them before you decide. Files open once their virus check clears them.',
      }}
    >
      {text !== null && text.trim() !== '' && <blockquote className={s.quote}>{text}</blockquote>}
      <FileList
        requestId={award.id}
        documents={files}
        canChange={false}
        empty={
          text === null || text.trim() === '' ? (
            <EmptyState
              variant="first-day"
              art="letter"
              size="sm"
              headline="No note or files"
              body="An entry can carry a note and supporting files. This one has neither; the people and amounts are above."
            />
          ) : (
            <p className={s.muted}>No files attached.</p>
          )
        }
      />
    </Card>
  );
}

/** X2: one over-budget pool's numbers, from before this award to after the top-up. */
export function LedgerCard({
  detail,
  draft,
}: {
  detail: ApprovalDetail;
  draft: TopUpDraft;
}): ReactElement {
  const { data: me } = useQuery(meQuery);
  // The award's fiscal year picks the ledger. An award read without it (an older server) still
  // shows what was left before it; the ledger rows need the year.
  const year = detail.award.fiscalYear ?? null;
  const { data: ledger } = useQuery({
    ...poolLedgerQuery(draft.pool.id, year ?? 0),
    enabled: year !== null,
    retry: false,
  });
  const demand = demandOf(detail, draft.pool.id);
  const before = draft.remainingAfter.cashCents + demand.cashCents;
  const balance = draft.remainingAfter.cashCents;
  const top = draft.cashCents ?? 0;
  const after = afterTopUp(draft);
  const name = draft.pool.name;
  const book =
    year === null || ledger === undefined
      ? null
      : overageLedger({
          ledger,
          detail,
          draft,
          // A Stock Admin's top-up is done for a named Executive (APP-148), never in their own right.
          approver: decidesOwn(detail)
            ? (me?.person.displayName ?? 'you')
            : `${me?.person.displayName ?? 'you'} for an Executive`,
          today: organizationDate(new Date().toISOString()),
        });
  // Before the ledger loads, the bars start from what was left before this award.
  const base = book?.budgetCents ?? before;
  const spent = book?.awardedCents ?? 0;
  const max = Math.max(1, base + top, spent + demand.cashCents);
  const pct = (cents: number): string =>
    `${Math.max(0, Math.min(100, (cents / max) * 100)).toFixed(1)}%`;
  const minus = (cents: number): string => (cents === 0 ? '$0' : formatMoney(-cents));
  const tiles: { label: ReturnType<typeof scoped>; value: string; tone?: string }[] = [
    ...(book === null
      ? [{ label: scoped(name, 'left before this award'), value: formatMoney(before) }]
      : [
          { label: scoped(name, `budget ${String(year)}`), value: formatMoney(book.budgetCents) },
          { label: scoped(name, 'awarded so far'), value: minus(book.awardedCents) },
        ]),
    { label: scoped(name, 'this award'), value: minus(demand.cashCents), tone: s.tileGold },
    { label: scoped(name, 'balance'), value: formatMoney(balance), tone: s.tileProblem },
    { label: scoped(name, 'top-up'), value: `+${formatMoney(top)}`, tone: s.tileDone },
    {
      label: scoped(name, 'after'),
      value: formatMoney(after.cashCents),
      tone: after.cashCents < 0 ? s.tileProblem : s.tileDone,
    },
  ];
  return (
    <Card
      title={year === null ? `${name} pool · this year` : `${name} pool · ${String(year)} ledger`}
      help={{
        shows: `The ${name} pool’s year: its budget, what’s been awarded so far, this award, the balance it leaves, the top-up and where the pool ends up, with every budget entry in the ledger below.`,
        canDo:
          'Change the top-up on the right. Back to zero is the default; any amount from zero up is allowed, and every top-up is logged.',
      }}
      aside={<span className={s.muted}>Cash · {name}</span>}
    >
      <div className={s.tiles}>
        {tiles.map((tile) => (
          <div key={tile.label} className={cx(s.tile, tile.tone)}>
            <p className={cx('eyebrow', s.tileLabel)}>{tile.label}</p>
            <p className={cx('disp', s.tileValue)}>{tile.value}</p>
          </div>
        ))}
      </div>
      {/* The bars repeat the tiles and the ledger for the eye; the numbers are in words above. */}
      <div aria-hidden="true">
        <div className={s.bars}>
          <span className={s.barLabel}>{book === null ? 'Left before' : 'Budget'}</span>
          <span className={s.bar}>
            <span className={s.barNavy} style={{ width: pct(base) }} />
            <span className={s.barGreen} style={{ width: pct(top) }} />
          </span>
          <span className={s.barLabel}>Awarded</span>
          <span className={cx(s.bar, s.barMarked)}>
            <span className={s.barSky} style={{ width: pct(spent) }} />
            <span className={s.barGold} style={{ width: pct(demand.cashCents) }} />
            <span className={s.barMark} style={{ left: pct(base) }} />
          </span>
        </div>
        <ul className={s.legend}>
          <li>
            <span className={cx(s.swatch, s.barNavy)} />
            {book === null ? 'Left before' : 'Budget'} {formatMoney(base)}
          </li>
          <li>
            <span className={cx(s.swatch, s.barGreen)} />
            Top-up
          </li>
          {book !== null && (
            <li>
              <span className={cx(s.swatch, s.barSky)} />
              Awarded so far
            </li>
          )}
          <li>
            <span className={cx(s.swatch, s.barGold)} />
            This award
          </li>
          <li>
            <span className={cx(s.swatch, s.swatchMark)} />
            Where the budget ends
          </li>
        </ul>
      </div>
      {book !== null && (
        <DataTable
          caption={`${name} pool ledger, ${String(year)}`}
          rows={book.rows}
          rowKey={(r) => r.id}
          minWidth={560}
          cards
          rowTone={(r) => (r.tone === 'award' ? 'problem' : r.tone === 'top-up' ? 'done' : null)}
          empty={{
            title: 'No budget entries yet.',
            body: 'The year’s budget, additions, moves and top-ups show here.',
            art: 'pool',
          }}
          columns={[
            { id: 'date', header: 'Date', nowrap: true, cell: (r) => r.date },
            {
              id: 'entry',
              header: 'Entry',
              card: 'title',
              cell: (r) => (
                <>
                  <span className={s.ledgerEntry}>
                    <span className={s.strong}>{r.entry}</span>
                    {r.tone === 'top-up' && (
                      <StatusPill tone="waiting" size="sm" label="With this approval" />
                    )}
                  </span>
                  <span className={s.sub}>{r.sub}</span>
                </>
              ),
            },
            {
              id: 'amount',
              header: 'Amount',
              numeric: true,
              nowrap: true,
              cell: (r) => (
                <span className={cx(r.amountCents > 0 && s.positive)}>
                  {signedMoney(r.amountCents)}
                </span>
              ),
            },
            {
              id: 'balance',
              header: 'Balance',
              numeric: true,
              nowrap: true,
              cell: (r) => (
                <span className={cx(s.strong, r.balanceCents < 0 && s.negative)}>
                  {formatMoney(r.balanceCents)}
                </span>
              ),
            },
          ]}
        />
      )}
      <p className={s.muted}>
        {topUpSentence(draft).text} No borrowing from another sector’s pool.
      </p>
    </Card>
  );
}

/** X2 and X3 when over budget: the top-up for each over-budget pool, back to zero by default. */
export function TopUpFields({
  drafts,
  onChange,
}: {
  drafts: readonly TopUpDraft[];
  onChange: (index: number, draft: TopUpDraft) => void;
}): ReactElement {
  return (
    <div className={s.topUps}>
      {drafts.map((draft, index) => {
        const sentence = topUpSentence(draft);
        return (
          <fieldset key={draft.pool.id} className={s.topUp}>
            <legend className={s.strong}>Add to the {draft.pool.name} pool</legend>
            <div className={s.topUpFields}>
              <div className={s.inlineField}>
                <span className={s.fieldName}>Cash</span>
                <AmountInput
                  label={`Top-up cash for ${draft.pool.name}`}
                  valueCents={draft.cashCents}
                  onChange={(cents) => {
                    onChange(index, { ...draft, cashCents: cents });
                  }}
                />
              </div>
              {draft.withOptions && (
                <div className={s.inlineField}>
                  <span className={s.fieldName}>Options</span>
                  <CountInput
                    label={`Top-up options for ${draft.pool.name}`}
                    value={draft.options}
                    onChange={(n) => {
                      onChange(index, { ...draft, options: n });
                    }}
                  />
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              isDisabled={isDefaultTopUp(draft)}
              onPress={() => {
                onChange(index, {
                  ...draft,
                  cashCents: draft.shortfall.cashCents,
                  options: draft.shortfall.options,
                });
              }}
            >
              Back to zero ·{' '}
              {amountWords(
                draft.shortfall.cashCents,
                draft.withOptions ? draft.shortfall.options : 0,
              )}
            </Button>
            <Notice tone={sentence.tone === 'problem' ? 'waiting' : 'done'}>{sentence.text}</Notice>
          </fieldset>
        );
      })}
    </div>
  );
}

/** "Any one Executive can approve…": the plain rules beside a top-up. */
/** The top-up's fixed rules. For a Stock Admin acting for an Executive, the ledger says for whom. */
export function TopUpRules({ acting = false }: { acting?: boolean }): ReactElement {
  return (
    <ul className={s.rules}>
      <li>Any one Executive can approve. The others see it’s done.</li>
      <li>
        {acting
          ? 'The top-up goes in the pool’s ledger for the year, with who did it, for whom, and why.'
          : 'The top-up goes in the pool’s ledger for the year, with who and why.'}
      </li>
      <li>No borrowing from another sector.</li>
    </ul>
  );
}

export function ChargeRuleCard(): ReactElement {
  return (
    <Card
      title="Charge rule"
      titleSize="sm"
      compact
      help={{
        shows: 'How the charge codes on a PAA or TAA are worked out.',
        canDo: 'Nothing to set here: codes fill in by themselves and move with a person’s pool.',
      }}
    >
      <p className={s.cardText}>
        People in the PAA/TAA pool: 0535-006 (OH) or 0713-006 (G&amp;A) and their home org, no
        project. People in the sector pool: the sector’s project, account and org.
      </p>
      <p className={s.muted}>Awards keep the codes they used.</p>
    </Card>
  );
}

export function HowItWorksCard({ steps }: { steps: readonly string[] }): ReactElement {
  return (
    <Card
      title="How this approval works"
      tone="dark"
      compact
      titleSize="sm"
      help={{
        shows: 'What happens before and after this decision.',
        canDo: 'Read it once; the decision itself is on the left.',
      }}
    >
      <ol className={s.steps2}>
        {steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </Card>
  );
}

/** "3 people · $4,500" with "of 8" when some are left out. */
export function includedWords(people: number, of: number): string {
  return people === of
    ? plural(people, 'person', 'people')
    : `${formatCount(people)} of ${formatCount(of)} people`;
}
